import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function Products() {
  const products = [
    {
      title: 'Concrete Curbs',
      description: 'Custom concrete curbing for driveways, parking lots, and landscaping with professional installation.',
      icon: 'ri-road-map-line',
    },
    {
      title: 'Recycled Asphalt',
      description: 'Eco-friendly recycled asphalt millings for driveways, roads, and base material at competitive prices.',
      icon: 'ri-recycle-line',
    },
    {
      title: 'Hot Tar & Chip',
      description: 'Durable tar and chip surfacing for driveways and rural roads with excellent traction and drainage.',
      icon: 'ri-drop-line',
    },
    {
      title: 'Gravel & Stone',
      description: 'Quality gravel, crushed stone, and decorative rock for driveways, paths, and landscaping projects.',
      icon: 'ri-stack-line',
    },
    {
      title: 'Base Materials',
      description: 'Crushed limestone, road base, and aggregate materials for proper pavement foundation.',
      icon: 'ri-layout-bottom-line',
    },
    {
      title: 'Asphalt Millings',
      description: 'Recycled asphalt pavement for cost-effective driveways and parking areas that compact and harden.',
      icon: 'ri-building-4-line',
    },
  ];

  const faqs = [
    {
      question: 'What are recycled asphalt millings and how much do they cost?',
      answer: 'Asphalt millings are recycled asphalt pavement ground into gravel-sized pieces. They cost $10-$20 per ton delivered in Lincoln NE, about half the cost of new asphalt. Millings compact and harden over time, creating a durable surface for driveways, parking areas, and rural roads. They\'re eco-friendly and budget-friendly.',
    },
    {
      question: 'How much does concrete curbing cost in Lincoln Nebraska?',
      answer: 'Concrete curbing costs $6-$12 per linear foot installed, depending on style and site conditions. Standard 6-inch curbs cost $6-$8 per foot. Decorative or stamped curbs cost $10-$15 per foot. A typical driveway needs 40-60 feet of curbing, costing $240-$720. Curbs provide edge support and improve drainage.',
    },
    {
      question: 'What is tar and chip paving?',
      answer: 'Tar and chip (also called chip seal) involves spraying hot liquid asphalt then spreading stone chips that embed in the tar. It costs $2-$5 per square foot, less than traditional asphalt. Tar and chip provides excellent traction, drainage, and rustic appearance. It lasts 7-10 years and is popular for rural driveways.',
    },
    {
      question: 'What type of gravel is best for driveways?',
      answer: 'Crushed limestone or crushed concrete (3/4 inch minus) works best for driveways. These materials compact well and provide stable base. River rock and pea gravel look nice but don\'t compact, causing ruts and shifting. We recommend 4-6 inches of compacted crushed stone for driveway base.',
    },
    {
      question: 'Do you deliver materials or just install?',
      answer: 'We provide both material delivery and complete installation services. Buy materials only if you\'re doing DIY work, or hire us for turnkey installation. We deliver throughout Lincoln and Lancaster County. Delivery fees vary by distance and quantity. Call for pricing and availability.',
    },
    {
      question: 'Can I use recycled asphalt for my driveway?',
      answer: 'Yes! Recycled asphalt millings make excellent driveways at half the cost of new asphalt. Spread 4-6 inches, compact with a roller, and the millings will bind together over time. They handle traffic well and last 10-15 years. We install recycled asphalt driveways throughout Lincoln NE.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-primary-500 text-sm mb-4 tracking-wider">Services / Products</p>
            <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Paving Products & Materials
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Quality concrete curbs, recycled asphalt, gravel, and base materials in Lincoln, Nebraska
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/4064ec80d0f2331809299b2c15bb19de.jpg"
                alt="Lincoln NE paving products and materials"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Quality Materials for Every Project
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                We supply premium paving materials for residential and commercial projects throughout Lincoln and Lancaster County. From concrete curbs to recycled asphalt, gravel to base materials, we provide everything needed for quality pavement construction.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Our products meet or exceed industry standards for strength, durability, and performance. We source materials from trusted local suppliers and deliver throughout the Lincoln area. Whether you need materials only or complete installation, we provide expert guidance and competitive pricing.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Serving contractors, property owners, and DIY customers for over 20 years, we offer material delivery, installation services, and expert advice. Our experienced team helps you choose the right products for your specific project requirements and budget.
              </p>
              <a href="tel:3084705637" className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                CALL (308) 470-5637
              </a>
            </div>
          </div>

          {/* Products Grid */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Available Products
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200 hover:border-primary-600 transition-all">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-6">
                    <i className={`${product.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {product.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {product.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Content */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Paving Products and Materials in Lincoln NE
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Quality materials are the foundation of quality pavement. Whether you're building a new driveway, repairing existing pavement, or tackling a commercial project, using the right products ensures lasting results. We supply premium paving materials that meet industry standards and perform well in Nebraska's challenging climate.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our product line includes everything needed for complete paving projects: concrete curbs for edge support, recycled asphalt for eco-friendly driveways, tar and chip for rural roads, gravel for drainage and base, and quality aggregate materials for proper foundation. We help customers choose the right products for their specific needs and budget.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Concrete Curbs and Edging
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Concrete curbs provide essential edge support for asphalt and concrete pavement. Without proper edging, pavement edges crumble and deteriorate from traffic and weather. Curbs contain the pavement, direct water drainage, and create clean, professional appearance.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We install several curb styles: standard 6-inch vertical curbs for driveways and parking lots, rolled curbs for easier vehicle access, decorative stamped curbs for upscale properties, and custom designs to match architectural features. Curbs are poured in place using professional forms and quality concrete.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Concrete curbing costs $6-$12 per linear foot installed. Standard curbs cost $6-$8 per foot, while decorative or stamped curbs cost $10-$15 per foot. A typical residential driveway needs 40-60 feet of curbing, costing $240-$720. Curbs last 30+ years with minimal maintenance.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Recycled Asphalt Millings
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Recycled asphalt millings are an eco-friendly, budget-friendly alternative to new asphalt. When old asphalt is removed during resurfacing or replacement, it's ground into gravel-sized pieces called millings. These millings retain asphalt binder that reactivates under heat and compaction, creating a durable surface.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Millings cost $10-$20 per ton delivered in Lincoln NE, about half the cost of new asphalt. A typical driveway needs 15-25 tons, costing $150-$500 versus $1,200-$2,400 for new asphalt. Millings are spread 4-6 inches thick, compacted with a roller, and allowed to cure. They harden over time, creating a solid surface that handles traffic well.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Recycled asphalt driveways last 10-15 years with proper installation and maintenance. They provide excellent drainage, resist weeds, and require minimal upkeep. Millings are popular for rural driveways, farm roads, parking areas, and budget-conscious projects. We deliver and install recycled asphalt throughout Lancaster County.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Hot Tar and Chip Surfacing
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Tar and chip (also called chip seal) is a cost-effective paving method popular for rural driveways and low-traffic roads. The process involves spraying hot liquid asphalt (tar) then immediately spreading stone chips that embed in the tar. A roller compacts the chips, creating a textured, durable surface.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Tar and chip costs $2-$5 per square foot, significantly less than traditional asphalt ($7-$12 per square foot). It provides excellent traction in all weather, superior drainage compared to smooth asphalt, and rustic appearance that complements rural properties. The stone chips come in various colors for custom looks.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Tar and chip surfaces last 7-10 years before needing renewal. They require minimal maintenance - just occasional stone chip replacement in high-traffic areas. This method is ideal for long driveways where traditional asphalt would be prohibitively expensive. We provide complete tar and chip installation throughout Lincoln and Lancaster County.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Gravel and Crushed Stone
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Gravel driveways are economical, attractive, and easy to maintain. We supply various gravel types: crushed limestone (best for driveways), crushed concrete (recycled and economical), pea gravel (decorative but doesn't compact), river rock (attractive for landscaping), and decorative stone in multiple colors.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              For driveways, we recommend 3/4 inch minus crushed limestone or crushed concrete. These materials compact well, provide stable surface, and handle traffic effectively. Spread 4-6 inches of gravel over compacted subgrade for best results. Gravel costs $30-$50 per ton delivered, with typical driveways needing 15-25 tons.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Gravel driveways need periodic maintenance: adding new gravel every 2-3 years, grading to maintain proper crown and drainage, and weed control. With proper care, gravel driveways last indefinitely. They're popular for rural properties, farm access roads, and budget-conscious homeowners.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Base Materials and Aggregate
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Proper base preparation is critical for pavement longevity. We supply quality base materials: crushed limestone (road base), recycled concrete base, Class 5 aggregate (gravel and sand mix), and drainage rock. These materials provide stable foundation that prevents settling, cracking, and premature failure.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Asphalt and concrete driveways need 4-6 inches of compacted base material. Poor soil or high water tables require 6-8 inches. Base material costs $15-$30 per ton delivered. Proper compaction is essential - we compact in 2-3 inch lifts to 95% density for maximum stability.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We also supply drainage materials: washed rock for French drains, filter fabric to prevent base migration, and drainage pipe for water management. Proper drainage prevents water accumulation that undermines pavement and causes failure.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Material Delivery and Installation
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We provide material delivery throughout Lincoln and Lancaster County. Delivery fees vary by distance and quantity - typically $50-$150 for local deliveries. We deliver bulk materials (gravel, millings, base) by the ton or truckload. Minimum orders may apply for delivery.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              For customers who prefer professional installation, we provide complete turnkey services. Our experienced crews handle site preparation, base installation, material placement, compaction, and finishing. Installation ensures proper techniques, equipment, and results that DIY projects often can't match.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We help customers choose between DIY and professional installation based on project scope, budget, and timeline. Small projects may be suitable for DIY with delivered materials. Larger or complex projects benefit from professional installation that ensures quality results and long-term performance.
            </p>
          </div>

          {/* Product Gallery */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Product Gallery
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <img
                src="https://readdy.ai/api/search-image?query=concrete%20curb%20installation%20showing%20fresh%20poured%20curbing%20along%20driveway%20edge%20with%20professional%20forms%20and%20smooth%20finish%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=600&height=400&seq=105&orientation=landscape"
                alt="Concrete curb installation"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
              <img
                src="https://readdy.ai/api/search-image?query=recycled%20asphalt%20millings%20driveway%20showing%20compacted%20black%20surface%20with%20gravel%20texture%20for%20residential%20property%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=600&height=400&seq=106&orientation=landscape"
                alt="Recycled asphalt millings"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
              <img
                src="https://readdy.ai/api/search-image?query=tar%20and%20chip%20driveway%20showing%20textured%20stone%20chip%20surface%20with%20rustic%20appearance%20for%20rural%20property%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=600&height=400&seq=107&orientation=landscape"
                alt="Tar and chip surface"
                className="w-full h-64 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Form */}
          <div className="bg-gray-50 p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-6 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Request Product Quote
            </h2>
            <p className="text-xl text-gray-700 text-center mb-8">
              Get pricing on materials and installation services
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}